import { Button, DialogTitle, Text, VStack } from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { FiTrash2 } from "react-icons/fi"

import { type ApiError, ContentService, type SubjectPublic } from "@/client"
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "@/components/ui/dialog"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"

interface DeleteSubjectProps {
  subject: SubjectPublic
}

const DeleteSubject = ({ subject }: DeleteSubjectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useForm()

  const deleteSubject = async (id: string) => {
    await ContentService.deleteSubject({ subjectId: id })
  }

  const mutation = useMutation({
    mutationFn: deleteSubject,
    onSuccess: () => {
      showSuccessToast("The subject was deleted successfully")
      setIsOpen(false)
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["subjects"] })
    },
  })

  const onSubmit = async () => {
    mutation.mutate(subject.id)
  }

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      role="alertdialog"
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" colorPalette="red">
          <FiTrash2 fontSize="16px" />
          Delete Subject
        </Button>
      </DialogTrigger>

      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>Delete Subject</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack align="stretch" gap={3}>
              <Text>
                Are you sure you want to delete the subject "
                <strong>{subject.name}</strong>"?
              </Text>

              <Text colorPalette="orange" fontWeight="semibold" fontSize="sm">
                ⚠️ Warning: If this subject has questions attached, deleting it
                will remove the subject from those questions.
              </Text>

              <Text fontSize="sm" color="gray.600">
                Questions will remain in the system but will no longer be
                categorized under this subject. This action cannot be undone.
              </Text>
            </VStack>
          </DialogBody>

          <DialogFooter gap={2}>
            <DialogActionTrigger asChild>
              <Button
                variant="subtle"
                colorPalette="gray"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              variant="solid"
              colorPalette="red"
              type="submit"
              loading={isSubmitting}
            >
              Delete Subject
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  )
}

export default DeleteSubject
